import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  enterpriseMemberListSchema,
  enterprisePolicySchema,
  workspaceEnterpriseLinkageSchema,
  type ApiError,
  type BoardVisibility,
  type EnterpriseBoardVisibilityPolicyPatch,
  type EnterpriseGuestInviteRule,
  type EnterpriseMember,
  type EnterprisePolicy,
  type ManagedMemberLifecycleBody,
  type WorkspaceEnterpriseLinkage,
} from '@molt/shared';
import { emptyResponseSchema, get, patch, post } from './api';
import { queryKeys } from './query-keys';

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/** `GET /enterprises/:id/policy` — Enterprise admin policy surface. */
export function getEnterprisePolicy(enterpriseId: string): Promise<EnterprisePolicy> {
  return get(
    `/enterprises/${encodeURIComponent(enterpriseId)}/policy`,
    enterprisePolicySchema,
  );
}

/**
 * `GET /enterprises/:id/members` — the Enterprise member list, `FR-PERM-034`.
 *
 * Every row carries `managed` and `deactivated`; `email` is `null` unless the
 * row is managed. The route folds a non-admin Enterprise member, an outsider
 * and an id that never existed into **one 404** — an Enterprise id must not be
 * probeable — so a caller reads a 404 as "there is nothing here for you" and
 * asks no further question.
 */
export function getEnterpriseMembers(enterpriseId: string): Promise<EnterpriseMember[]> {
  return get(
    `/enterprises/${encodeURIComponent(enterpriseId)}/members`,
    enterpriseMemberListSchema,
  );
}

/**
 * `GET /workspaces/:id/enterprise` — is this workspace inside an Enterprise,
 * and what is the caller's own role in it.
 *
 * Answers **200 with `enterpriseId: null`** for a workspace that has no
 * Enterprise; that is a fact, not an absence, and is why this is not a 404.
 * A caller who is not a member of the workspace gets 404 from the workspace
 * permission module, which decides nothing and folds every refusal into one
 * code.
 */
export function getWorkspaceEnterpriseLinkage(
  workspaceId: string,
): Promise<WorkspaceEnterpriseLinkage> {
  return get(
    `/workspaces/${encodeURIComponent(workspaceId)}/enterprise`,
    workspaceEnterpriseLinkageSchema,
  );
}

/** `PATCH /enterprises/:id/policy/default-board-visibility` — field-scoped. */
export function updateEnterpriseDefaultBoardVisibility(
  enterpriseId: string,
  visibility: BoardVisibility,
): Promise<EnterprisePolicy> {
  return patch(
    `/enterprises/${encodeURIComponent(enterpriseId)}/policy/default-board-visibility`,
    enterprisePolicySchema,
    { visibility },
  );
}

/** `PATCH /enterprises/:id/policy/guest-invite-rule` — field-scoped. */
export function updateEnterpriseGuestInviteRule(
  enterpriseId: string,
  guestInviteRule: EnterpriseGuestInviteRule,
): Promise<EnterprisePolicy> {
  return patch(
    `/enterprises/${encodeURIComponent(enterpriseId)}/policy/guest-invite-rule`,
    enterprisePolicySchema,
    { guestInviteRule },
  );
}

/** `PATCH /enterprises/:id/board-visibility-policies/:visibility` — field-scoped. */
export function updateEnterpriseBoardVisibilityPolicy(
  enterpriseId: string,
  visibility: BoardVisibility,
  body: EnterpriseBoardVisibilityPolicyPatch,
): Promise<EnterprisePolicy> {
  return patch(
    `/enterprises/${encodeURIComponent(enterpriseId)}/board-visibility-policies/${encodeURIComponent(visibility)}`,
    enterprisePolicySchema,
    body,
  );
}

/** `POST /enterprises/:id/members/:userId/deactivate` — managed target only. */
export function deactivateManagedMember(
  enterpriseId: string,
  userId: string,
): Promise<null> {
  const body: ManagedMemberLifecycleBody = { confirm: true };
  return post(
    `/enterprises/${encodeURIComponent(enterpriseId)}/members/${encodeURIComponent(userId)}/deactivate`,
    emptyResponseSchema,
    body,
  );
}

/** `POST /enterprises/:id/members/:userId/reactivate` — managed target only. */
export function reactivateManagedMember(
  enterpriseId: string,
  userId: string,
): Promise<null> {
  const body: ManagedMemberLifecycleBody = { confirm: true };
  return post(
    `/enterprises/${encodeURIComponent(enterpriseId)}/members/${encodeURIComponent(userId)}/reactivate`,
    emptyResponseSchema,
    body,
  );
}

// ---------------------------------------------------------------------------
// Query options
// ---------------------------------------------------------------------------

export function enterprisePolicyQueryOptions(
  enterpriseId: string,
): UseQueryOptions<EnterprisePolicy, ApiError> {
  return {
    queryKey: queryKeys.enterprises.policy(enterpriseId),
    queryFn: () => getEnterprisePolicy(enterpriseId),
  };
}

export function enterpriseMembersQueryOptions(
  enterpriseId: string,
): UseQueryOptions<EnterpriseMember[], ApiError> {
  return {
    queryKey: queryKeys.enterprises.members(enterpriseId),
    queryFn: () => getEnterpriseMembers(enterpriseId),
  };
}

export function workspaceEnterpriseLinkageQueryOptions(
  workspaceId: string,
): UseQueryOptions<WorkspaceEnterpriseLinkage, ApiError> {
  return {
    queryKey: queryKeys.workspaces.enterprise(workspaceId),
    queryFn: () => getWorkspaceEnterpriseLinkage(workspaceId),
  };
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useEnterprisePolicy(
  enterpriseId: string,
  enabled: boolean,
): UseQueryResult<EnterprisePolicy, ApiError> {
  return useQuery({ ...enterprisePolicyQueryOptions(enterpriseId), enabled });
}

export function useEnterpriseMembers(
  enterpriseId: string,
  enabled = true,
): UseQueryResult<EnterpriseMember[], ApiError> {
  return useQuery({ ...enterpriseMembersQueryOptions(enterpriseId), enabled });
}

export function useWorkspaceEnterpriseLinkage(
  workspaceId: string,
  enabled = true,
): UseQueryResult<WorkspaceEnterpriseLinkage, ApiError> {
  return useQuery({ ...workspaceEnterpriseLinkageQueryOptions(workspaceId), enabled });
}

function useEnterprisePolicyMutation<TVariables>(
  enterpriseId: string,
  mutationFn: (variables: TVariables) => Promise<EnterprisePolicy>,
): UseMutationResult<EnterprisePolicy, ApiError, TVariables> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.enterprises.policy(enterpriseId),
        exact: true,
      });
    },
  });
}

export function useUpdateEnterpriseDefaultBoardVisibility(
  enterpriseId: string,
): UseMutationResult<EnterprisePolicy, ApiError, BoardVisibility> {
  return useEnterprisePolicyMutation(enterpriseId, (visibility: BoardVisibility) =>
    updateEnterpriseDefaultBoardVisibility(enterpriseId, visibility),
  );
}

export function useUpdateEnterpriseGuestInviteRule(
  enterpriseId: string,
): UseMutationResult<EnterprisePolicy, ApiError, EnterpriseGuestInviteRule> {
  return useEnterprisePolicyMutation(
    enterpriseId,
    (guestInviteRule: EnterpriseGuestInviteRule) =>
      updateEnterpriseGuestInviteRule(enterpriseId, guestInviteRule),
  );
}

export function useUpdateEnterpriseBoardVisibilityPolicy(
  enterpriseId: string,
): UseMutationResult<
  EnterprisePolicy,
  ApiError,
  { visibility: BoardVisibility; body: EnterpriseBoardVisibilityPolicyPatch }
> {
  return useEnterprisePolicyMutation(
    enterpriseId,
    ({
      visibility,
      body,
    }: {
      visibility: BoardVisibility;
      body: EnterpriseBoardVisibilityPolicyPatch;
    }) => updateEnterpriseBoardVisibilityPolicy(enterpriseId, visibility, body),
  );
}

/**
 * Deactivate and reactivate both invalidate the **member list**, not the
 * policy: `deactivated` lives on `enterpriseMemberSchema`, and the policy
 * document does not change when a person's account state does.
 *
 * Written as a shared helper for the reason the two hooks below existed
 * without one — until `queryKeys.enterprises.members` existed there was no key
 * to invalidate, so neither hook had an `onSuccess`, and an admin who
 * deactivated somebody watched the row they had just changed go on reading
 * active until the page was reloaded.
 */
function useManagedMemberLifecycleMutation(
  enterpriseId: string,
  mutationFn: (variables: { userId: string }) => Promise<null>,
): UseMutationResult<null, ApiError, { userId: string }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.enterprises.members(enterpriseId),
        exact: true,
      });
    },
  });
}

export function useDeactivateManagedMember(
  enterpriseId: string,
): UseMutationResult<null, ApiError, { userId: string }> {
  return useManagedMemberLifecycleMutation(
    enterpriseId,
    ({ userId }: { userId: string }) => deactivateManagedMember(enterpriseId, userId),
  );
}

export function useReactivateManagedMember(
  enterpriseId: string,
): UseMutationResult<null, ApiError, { userId: string }> {
  return useManagedMemberLifecycleMutation(
    enterpriseId,
    ({ userId }: { userId: string }) => reactivateManagedMember(enterpriseId, userId),
  );
}
